import { useCallback, useEffect, useMemo } from 'react';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useThrottleFn } from 'ahooks';
import { useUserLoggedIn } from '~/modules/user';
import { useConversation } from '../context';
import { useRealTimeChat } from './use-real-time-chat';

/**
 * How long to show the typing indicator after receiving a typing event (in ms).
 * The indicator will disappear after this timeout if no new typing events are received.
 */
const TYPING_TIMEOUT_MS = 3000;

/**
 * Represents a participant who is currently typing, with their timeout ID.
 */
interface TypingParticipant {
    participantID: number;
    participantName: string;
    timeoutId: ReturnType<typeof setTimeout>;
}

/**
 * Global atom for tracking typing participants across all conversations.
 */
const typingStateAtom = atom<Map<number, TypingParticipant[]>>(new Map());

/**
 * Hook to get the typing participants for a specific conversation. Must be used
 * within a `ConversationProvider`.
 * @returns The list of participants who are currently typing.
 */
export function useTypingIndicator() {
    const { conversationID, selfParticipant } = useConversation();
    const typingState = useAtomValue(typingStateAtom);

    return useMemo(() => {
        if (conversationID <= 0) {
            return [];
        }
        const participants = typingState.get(conversationID);
        if (!participants?.length) {
            return [];
        }
        return participants
            .filter((p) => String(p.participantID) !== selfParticipant?.ID)
            .map((p) => ({
                participantID: p.participantID,
                participantName: p.participantName,
            }));
    }, [conversationID, typingState, selfParticipant?.ID]);
}

/**
 * Hook to manage typing indicator state globally. This should be called once
 * in a parent component (e.g. AppPage) to handle webSocket messages and
 * update the typing state.
 */
export function useTypingIndicatorManager() {
    const { isLoggedIn } = useUserLoggedIn();
    const setTypingState = useSetAtom(typingStateAtom);

    function clearTypingParticipant(
        conversationID: number,
        participantID: number,
    ) {
        setTypingState((prev) => {
            const participants = prev.get(conversationID);
            if (!participants) {
                return prev;
            }
            const filtered = participants.filter(
                (p) => p.participantID !== participantID,
            );
            if (filtered.length === participants.length) {
                return prev;
            }
            const next = new Map(prev);
            if (filtered.length === 0) {
                next.delete(conversationID);
            } else {
                next.set(conversationID, filtered);
            }
            return next;
        });
    }

    // Update the typing state when a message is received from the WS server.
    useRealTimeChat({
        onMessage: (message) => {
            if (message.type !== 'chat/typing') return;
            setTypingState((prev) => {
                const participants = prev.get(message.conversation_id) ?? [];
                // Clear existing timeout for this participant if any.
                const existing = participants.find(
                    (p) => p.participantID === message.participant_id,
                );
                if (existing) {
                    clearTimeout(existing.timeoutId);
                }
                // Create new timeout to clear the typing indicator.
                const timeoutId = setTimeout(() => {
                    clearTypingParticipant(
                        message.conversation_id,
                        message.participant_id,
                    );
                }, TYPING_TIMEOUT_MS);

                const newParticipant: TypingParticipant = {
                    participantID: message.participant_id,
                    participantName: message.participant_name,
                    timeoutId,
                };

                const next = new Map(prev);
                if (existing) {
                    // Update existing participant's timeout.
                    next.set(
                        message.conversation_id,
                        participants.map((p) => {
                            return p.participantID === message.participant_id
                                ? newParticipant
                                : p;
                        }),
                    );
                } else {
                    // Add new participant.
                    next.set(message.conversation_id, [
                        ...participants,
                        newParticipant,
                    ]);
                }
                return next;
            });
        },
    });

    // Clear global state and timeouts on logout.
    useEffect(() => {
        if (!isLoggedIn) {
            setTypingState((state) => {
                for (const participants of state.values()) {
                    for (const participant of participants) {
                        clearTimeout(participant.timeoutId);
                    }
                }
                return new Map();
            });
        }
    }, [isLoggedIn, setTypingState]);
}

/**
 * Hook to clear typing indicators for a specific conversation.
 * Useful when the current user sends a message (they stop typing).
 * @returns Function to clear typing indicator for a participant in a conversation.
 */
export function useClearTypingIndicator() {
    const setTypingState = useSetAtom(typingStateAtom);

    return useCallback(
        (conversationID: number, participantID: number) => {
            setTypingState((prev) => {
                const participants = prev.get(conversationID);
                if (!participants) {
                    return prev;
                }
                const participant = participants.find(
                    (p) => p.participantID === participantID,
                );
                if (participant) {
                    clearTimeout(participant.timeoutId);
                }
                const filtered = participants.filter(
                    (p) => p.participantID !== participantID,
                );
                if (filtered.length === participants.length) {
                    return prev;
                }
                const next = new Map(prev);
                if (filtered.length === 0) {
                    next.delete(conversationID);
                } else {
                    next.set(conversationID, filtered);
                }
                return next;
            });
        },
        [setTypingState],
    );
}

/**
 * Hook to send the typing message to the websocket server. This will notify
 * the other participants in the conversation that the current user is typing.
 * Must be used within a `ConversationProvider`.
 * @returns Function to send the typing message to the websocket server.
 */
export function useSendTypingIndicator() {
    const { conversationID, professionalID } = useConversation();
    const { sendJsonMessage, isConnected } = useRealTimeChat();

    const { run: sendTypingIndicator } = useThrottleFn(
        () => {
            if (!isConnected || conversationID <= 0) {
                return;
            }
            sendJsonMessage({
                type: 'chat/typing',
                conversation_id: conversationID,
                professional_id: professionalID,
            });
        },
        // Throttle to send at most once per 1.5 seconds.
        { wait: 1500, leading: true, trailing: false },
    );

    return sendTypingIndicator;
}
